import type { CashAdvancesCustomerSchemas, OffersSchemas } from "api-types";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import dayjs from "dayjs";

import { combineOfferAndCashAdvance } from "../utils";

dayjs.extend(isSameOrAfter);

interface Collection {
  trancheNk: string;
  collectionAmountInLowestDenomination: number;
  outstandingBalanceInLowestDenomination: number;
}

/**
 * NOTE: This fixture only supports creating collections schedules for stacked scenarios.
 */
export class CollectionScheduleFixture {
  private collections: Record<string, Collection[]> = {};

  constructor(
    offer: OffersSchemas.OfferDisplaySchema,
    cashAdvanceSummary?: CashAdvancesCustomerSchemas.CashAdvanceSummarySchema
  ) {
    const params = combineOfferAndCashAdvance(offer, cashAdvanceSummary);

    if (params.collectionMode === "blended") {
      return;
    }

    const nextCollectionStartDate = dayjs(params.nextCollectionStartDate);
    for (const tranche of params.tranches) {
      const trancheDeploymentDate = dayjs(tranche.deploymentDate);

      /*
       * We enforce the tranche deployment date to be the day before the collection start date to make producing
       * the collection schedule easier and ensure that the data is more representative.
       *
       * .diff() is the same as subtracting the two dates, but it returns a number instead of a Dayjs object.
       */
      const daysAfterCollectionStartDate = trancheDeploymentDate.add(1, "day").diff(nextCollectionStartDate, "day");
      if (daysAfterCollectionStartDate % params.collectionFrequency !== 0) {
        throw new Error(
          `Tranche ${tranche.trancheNk} start date should be ${
            daysAfterCollectionStartDate < 0
              ? (daysAfterCollectionStartDate * -1) % params.collectionFrequency
              : params.collectionFrequency - (daysAfterCollectionStartDate % params.collectionFrequency)
          } days later to align with the collection frequency`
        );
      }

      // If the tranche is already deployed, use the outstanding balance to calculate the number of collections
      const amountToCollectInLowestDenomination =
        tranche.outstandingBalanceInLowestDenomination > 0
          ? tranche.outstandingBalanceInLowestDenomination
          : tranche.totalRemittanceInLowestDenomination;

      // If the tranche is already deployed, start the collection schedule from the next collection start date
      const collectionStartDateForTranche =
        daysAfterCollectionStartDate < 0 ? nextCollectionStartDate : trancheDeploymentDate.add(1, "day");

      const numCollectionsForTranche = Math.floor(
        amountToCollectInLowestDenomination /
          (tranche.dailyCollectionFixedInLowestDenomination * params.collectionFrequency)
      );

      for (let i = 1; i < numCollectionsForTranche + 1; i++) {
        const collectionStartDate = collectionStartDateForTranche.add(i * params.collectionFrequency, "day");
        const collectionAmountInLowestDenomination =
          tranche.dailyCollectionFixedInLowestDenomination * params.collectionFrequency;
        const outstandingBalanceInLowestDenomination =
          amountToCollectInLowestDenomination - i * collectionAmountInLowestDenomination;

        if (this.collections[collectionStartDate.format("YYYY-MM-DD")]) {
          this.collections[collectionStartDate.format("YYYY-MM-DD")].push({
            trancheNk: tranche.trancheNk,
            collectionAmountInLowestDenomination,
            outstandingBalanceInLowestDenomination,
          });
        } else {
          this.collections[collectionStartDate.format("YYYY-MM-DD")] = [
            {
              trancheNk: tranche.trancheNk,
              collectionAmountInLowestDenomination,
              outstandingBalanceInLowestDenomination,
            },
          ];
        }
      }
    }
  }

  build(): CashAdvancesCustomerSchemas.ProjectedAdvanceCollectionOut[] {
    const result: CashAdvancesCustomerSchemas.ProjectedAdvanceCollectionOut[] = [];

    const sortedDates = Object.keys(this.collections).sort((a, b) => {
      return dayjs(a).isBefore(dayjs(b)) ? -1 : 1;
    });

    sortedDates.forEach((date) => {
      const collection = this.collections[date];
      result.push({
        forDate: date,
        tranches: collection.map(
          (item): CashAdvancesCustomerSchemas.TrancheAllocationStateOut => ({
            trancheNk: item.trancheNk,
            collectionAmountInLowestDenomination: item.collectionAmountInLowestDenomination,
            outstandingBalanceInLowestDenomination: item.outstandingBalanceInLowestDenomination,
          })
        ),
      });
    });

    return result;
  }
}
